// src/components/AmazonFeeBreakdown.js
import React from 'react';
import { TrendingUp, TrendingDown, Package } from 'lucide-react';

const fmtUSD = (v) =>
  v === null || v === undefined || !isFinite(v)
    ? '—'
    : `${v < 0 ? '-' : ''}$${Math.abs(v).toFixed(2)}`;

const fmtPct = (v) =>
  v === null || v === undefined || !isFinite(v) ? '—' : `${(v * 100).toFixed(1)}%`;

function DonutChart({ segments, size = 140 }) {
  const total = segments.reduce((s, seg) => s + Math.max(0, seg.value), 0);
  if (total <= 0) {
    return (
      <div
        className="flex items-center justify-center rounded-full border-2 border-dashed border-slate-700 text-slate-500 text-xs"
        style={{ width: size, height: size }}
      >
        No data
      </div>
    );
  }

  const radius = size / 2 - 12;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="block">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="rgb(30 41 59)"
        strokeWidth="18"
      />
      {segments.map((seg, i) => {
        const value = Math.max(0, seg.value);
        if (value <= 0) return null;
        const dash = (value / total) * circumference;
        const gap = circumference - dash;
        const circle = (
          <circle
            key={i}
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={seg.color}
            strokeWidth="18"
            strokeDasharray={`${dash} ${gap}`}
            strokeDashoffset={-offset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        );
        offset += dash;
        return circle;
      })}
    </svg>
  );
}

function MetricRow({ label, value, positive, tone }) {
  const toneClass =
    tone === 'good'
      ? 'text-emerald-400'
      : tone === 'bad'
      ? 'text-red-400'
      : 'text-white';
  return (
    <div className="flex items-baseline justify-between py-1.5">
      <span className="text-sm text-slate-400">{label}</span>
      <span className={`text-xl font-bold tabular-nums ${toneClass}`}>{value}</span>
    </div>
  );
}

function LineItem({ label, value, color, isNegative }) {
  return (
    <div className="flex items-center justify-between py-1 text-sm">
      <span className="flex items-center gap-2 text-slate-300">
        {color && (
          <span
            className="inline-block w-2.5 h-2.5 rounded-sm"
            style={{ backgroundColor: color }}
          />
        )}
        {label}
      </span>
      <span
        className={`tabular-nums ${
          isNegative ? 'text-red-400' : 'text-slate-200'
        }`}
      >
        {fmtUSD(value)}
      </span>
    </div>
  );
}

export default function AmazonFeeBreakdown({ result, title, hasInputs }) {
  if (!hasInputs) {
    return (
      <div className="bg-slate-800/50 backdrop-blur-md rounded-xl border border-slate-700/50 p-6">
        {title && (
          <div className="text-sm font-semibold text-slate-300 mb-4 uppercase tracking-wider">
            {title}
          </div>
        )}
        <div className="flex flex-col items-center justify-center py-10 text-slate-500">
          <Package size={40} className="mb-3 opacity-50" />
          <p className="text-sm text-center">
            Enter product cost and sale price
            <br />
            to see profit calculations
          </p>
        </div>
      </div>
    );
  }

  const {
    productCost,
    referralFee,
    fulfillmentFee,
    storageFee,
    shippingCost,
    ppcCost,
    miscCost,
    totalFees,
    netProfit,
    margin,
    roi,
    breakEvenPrice,
  } = result;

  const isProfit = netProfit >= 0;

  const lineItems = [
    { label: 'Product cost',   value: productCost,    color: '#64748b' },
    { label: 'Referral fee',   value: referralFee,    color: '#f59e0b' },
    { label: 'Fulfillment fee', value: fulfillmentFee, color: '#6366f1' },
    { label: 'Storage fee',    value: storageFee,     color: '#8b5cf6' },
    { label: 'Shipping',       value: shippingCost,   color: '#06b6d4' },
    { label: 'PPC / ads',      value: ppcCost,        color: '#ec4899' },
    { label: 'Misc / prep',    value: miscCost,       color: '#94a3b8' },
  ].filter((item) => item.value > 0);

  const segments = [
    ...lineItems,
    {
      label: 'Net profit',
      value: Math.max(0, netProfit),
      color: isProfit ? '#10b981' : '#ef4444',
    },
  ];

  return (
    <div className="bg-slate-800/50 backdrop-blur-md rounded-xl border border-slate-700/50 p-5 sm:p-6 animate-fade-in">
      {title && (
        <div className="text-sm font-semibold text-slate-300 mb-4 uppercase tracking-wider flex items-center gap-2">
          {isProfit ? (
            <TrendingUp size={16} className="text-emerald-400" />
          ) : (
            <TrendingDown size={16} className="text-red-400" />
          )}
          {title}
        </div>
      )}

      {/* Key metrics */}
      <div className="space-y-1 mb-5 pb-5 border-b border-slate-700/50">
        <MetricRow
          label="Net profit"
          value={fmtUSD(netProfit)}
          tone={isProfit ? 'good' : 'bad'}
        />
        <MetricRow label="Margin" value={fmtPct(margin)} tone={isProfit ? 'good' : 'bad'} />
        <MetricRow label="ROI" value={fmtPct(roi)} tone={isProfit ? 'good' : 'bad'} />
        <MetricRow label="Break-even price" value={fmtUSD(breakEvenPrice)} />
      </div>

      {/* Donut chart */}
      <div className="flex justify-center mb-5">
        <DonutChart segments={segments} />
      </div>

      {/* Fee breakdown */}
      <div className="bg-slate-900/50 rounded-lg p-4">
        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
          Fee breakdown
        </div>
        <div className="space-y-0.5">
          {lineItems.map((item) => (
            <LineItem key={item.label} {...item} />
          ))}
          <div className="border-t border-slate-700/50 mt-2 pt-2 flex items-center justify-between text-sm font-semibold">
            <span className="text-slate-200">Total fees</span>
            <span className="tabular-nums text-slate-100">{fmtUSD(totalFees)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
